import React, { useState, useEffect } from 'react';
import { RaceMeeting, Race, Runner } from '../types';
import RaceMeetingCard from './RaceMeetingCard';
import RaceCard from './RaceCard';
import SummaryStats from './SummaryStats';
import SimpleSlider from './SimpleSlider';
import './MeetingList.css';

interface MeetingListProps {
  meetings: RaceMeeting[];
  runners: Runner[];
}

type ViewMode = 'grouped' | 'chronological';
type FilterMode = 'all' | 'pending' | 'won' | 'lost';
type ConfidenceFilter = 'all' | 'very high' | 'high' | 'medium';

interface ScoreFilters {
  overall: [number, number];
  horse: [number, number];
  jockey: [number, number];
  trainer: [number, number];
  owner: [number, number];
}

const MeetingList: React.FC<MeetingListProps> = ({ meetings, runners }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('grouped');
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [confidenceFilter, setConfidenceFilter] = useState<ConfidenceFilter>('all');
  const [scoreFilters, setScoreFilters] = useState<ScoreFilters>({
    overall: [0, 100],
    horse: [0, 100],
    jockey: [0, 100],
    trainer: [0, 100],
    owner: [0, 100]
  });
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(false);
  const [allCollapsed, setAllCollapsed] = useState<boolean>(false);

  // Set initial score filter ranges based on actual data
  useEffect(() => {
    if (allRacesSorted.length > 0) {
      setScoreFilters({
        overall: getScoreRange('overall'),
        horse: getScoreRange('horse'),
        jockey: getScoreRange('jockey'),
        trainer: getScoreRange('trainer'),
        owner: getScoreRange('owner')
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetings]);



  const handleToggleAllCollapse = () => {
    setAllCollapsed(!allCollapsed);
  };


  // Helper function to determine race result status
  const getRaceStatus = (race: Race): 'won' | 'lost' | 'pending' => {
    if (!race.race_result || !race.ai_selection) return 'pending';
    const normalizeName = (name: string) => name.replace(/\s*\([^)]*\)\s*/g, '').trim();
    return normalizeName(race.race_result) === normalizeName(race.ai_selection) ? 'won' : 'lost';
  };

  // Get all races sorted by time for chronological view
  const allRacesSorted = meetings
    .flatMap(meeting => meeting.races)
    .sort((a, b) => a.race_time.localeCompare(b.race_time));

  // Calculate score ranges for sliders
  const getScoreRange = (scoreType: keyof ScoreFilters): [number, number] => {
    const scores = allRacesSorted.map(race => race[`ai_${scoreType}_score` as keyof Race] as number)
      .filter(score => typeof score === 'number' && !isNaN(score));
    if (scores.length === 0) return [0, 100];
    return [Math.min(...scores), Math.max(...scores)] as [number, number];
  };

  // Update score filter
  const updateScoreFilter = (scoreType: keyof ScoreFilters, value: [number, number]) => {
    setScoreFilters(prev => ({
      ...prev,
      [scoreType]: value
    }));
  };

  // Filter races based on all selected filters
  const filteredRaces = allRacesSorted.filter(race => {
    // Result filter
    if (filterMode !== 'all' && getRaceStatus(race) !== filterMode) {
      return false;
    }

    // Confidence filter
    if (confidenceFilter !== 'all' && race.ai_confidence.toLowerCase() !== confidenceFilter) {
      return false;
    }

    // Score filters
    if (race.ai_overall_score < scoreFilters.overall[0] || race.ai_overall_score > scoreFilters.overall[1]) {
      return false;
    }
    if (race.ai_horse_score < scoreFilters.horse[0] || race.ai_horse_score > scoreFilters.horse[1]) {
      return false;
    }
    if (race.ai_jockey_score < scoreFilters.jockey[0] || race.ai_jockey_score > scoreFilters.jockey[1]) {
      return false;
    }
    if (race.ai_trainer_score < scoreFilters.trainer[0] || race.ai_trainer_score > scoreFilters.trainer[1]) {
      return false;
    }
    if (race.ai_owner_score < scoreFilters.owner[0] || race.ai_owner_score > scoreFilters.owner[1]) {
      return false;
    }

    return true;
  });

  return (
    <div className="meeting-list-container">
      <SummaryStats meetings={meetings} races={allRacesSorted} />
      <div className="view-controls">
        <h2>Today's Qualifying Races ({allRacesSorted.length})</h2>
        <div className="view-controls-row">
          <div className="view-toggle">
            <button 
              className={`toggle-btn ${viewMode === 'grouped' ? 'active' : ''}`}
              onClick={() => setViewMode('grouped')}
            >
              By Meeting
            </button>
            <button 
              className={`toggle-btn ${viewMode === 'chronological' ? 'active' : ''}`}
              onClick={() => setViewMode('chronological')}
            >
              By Time
            </button>
          </div>
          <button 
            className="collapse-all-btn"
            onClick={handleToggleAllCollapse}
            title={allCollapsed ? "Show all race details" : "Collapse all race details"}
          >
            {allCollapsed ? 'Show All' : 'Collapse All'}
          </button>
        </div>
      </div>

      {viewMode === 'grouped' ? (
        <div className="meetings-section">
          <div className="meetings-grid">
            {meetings.map((meeting) => (
              <RaceMeetingCard
                key={meeting.meeting_id}
                meeting={meeting}
                runners={runners}
                allCollapsed={allCollapsed}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="chronological-section">
          <div className="filter-controls">
            <div className="filter-header">
              <h3>Filters</h3>
              <button 
                className="filter-toggle"
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                aria-label={filtersExpanded ? "Collapse filters" : "Expand filters"}
              >
                {filtersExpanded ? '−' : '+'}
              </button>
            </div>
            
            {filtersExpanded && (
              <>
                <div className="filter-row">
                  <div className="filter-group">
                    <label htmlFor="race-filter">Result:</label>
                    <select 
                      id="race-filter"
                      value={filterMode} 
                      onChange={(e) => setFilterMode(e.target.value as FilterMode)}
                      className="filter-dropdown"
                    >
                      <option value="all">All ({allRacesSorted.length})</option>
                      <option value="pending">Pending ({allRacesSorted.filter(r => getRaceStatus(r) === 'pending').length})</option>
                      <option value="won">Won ({allRacesSorted.filter(r => getRaceStatus(r) === 'won').length})</option>
                      <option value="lost">Lost ({allRacesSorted.filter(r => getRaceStatus(r) === 'lost').length})</option>
                    </select>
                  </div>
                  
                  <div className="filter-group">
                    <label htmlFor="confidence-filter">Confidence:</label>
                    <select 
                      id="confidence-filter"
                      value={confidenceFilter} 
                      onChange={(e) => setConfidenceFilter(e.target.value as ConfidenceFilter)}
                      className="filter-dropdown"
                    >
                      <option value="all">All</option>
                      <option value="very high">Very High</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                    </select>
                  </div>
                </div>
                
                <div className="score-filters">
                  <h4>Score Filters</h4>
                  <div className="score-sliders">
                  <SimpleSlider
                    label="Overall Score"
                    value={scoreFilters.overall}
                    min={getScoreRange('overall')[0]}
                    max={getScoreRange('overall')[1]}
                    onChange={(value) => updateScoreFilter('overall', value)}
                  />
                  <SimpleSlider
                    label="Horse Score"
                    value={scoreFilters.horse}
                    min={getScoreRange('horse')[0]}
                    max={getScoreRange('horse')[1]}
                    onChange={(value) => updateScoreFilter('horse', value)}
                  />
                  <SimpleSlider
                    label="Jockey Score"
                    value={scoreFilters.jockey}
                    min={getScoreRange('jockey')[0]}
                    max={getScoreRange('jockey')[1]}
                    onChange={(value) => updateScoreFilter('jockey', value)}
                  />
                  <SimpleSlider
                    label="Trainer Score"
                    value={scoreFilters.trainer}
                    min={getScoreRange('trainer')[0]}
                    max={getScoreRange('trainer')[1]}
                    onChange={(value) => updateScoreFilter('trainer', value)}
                  />
                  <SimpleSlider
                    label="Owner Score"
                    value={scoreFilters.owner}
                    min={getScoreRange('owner')[0]}
                    max={getScoreRange('owner')[1]}
                    onChange={(value) => updateScoreFilter('owner', value)}
                  />
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="races-grid">
            {filteredRaces.map((race) => (
              <RaceCard
                key={race.race_id}
                race={race}
                runners={runners}
                allCollapsed={allCollapsed}
              />
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default MeetingList;
